/**
 * Invoice detail: the customer portal registration code (tracker item 45, phase 4). ADMIN and
 * OFFICE_MANAGER only (the code is never shown to anyone else). An issued invoice carries a code that
 * customers type, with their email, to create an online account linked to the invoice's customer. It is on
 * the printed invoice, the public invoice page and the invoice email. Regenerate replaces it; the old
 * code stops working at once, accounts already registered with it keep working. Asks first.
 */
import React, { useMemo, useState } from 'react';
import { createAPIClient, IInvoicesApi } from '../api';
import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { primaryButtonClass, secondaryButtonClass } from './formStyles';
import { Toast } from './Toast';

interface Props {
  invoice: Pick<types.Invoice, 'id' | 'status' | 'portalCode'>;
  /** The invoice came back with a new code */
  onRegenerated: (code: string | null) => void;
}

export function InvoicePortalCodePanel({ invoice, onRegenerated }: Props) {
  const api = useMemo<IInvoicesApi>(() => createAPIClient(), []);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const issued = invoice.status !== types.InvoiceStatus.DRAFT && invoice.status !== types.InvoiceStatus.VOID;
  if (!issued) return null;

  async function regenerate() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await api.regenerateInvoicePortalCode(invoice.id);
      onRegenerated(updated.portalCode ?? null);
      setConfirming(false);
      setNotice('A new portal code was created. The old one no longer works.');
    } catch (cause) {
      setConfirming(false);
      setError(getErrorCode(cause) === 'INVOICE_NOT_ISSUED' ? 'Only an issued invoice has a portal code.' : getErrorMessage(cause, 'Could not create a new portal code'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="portal-code-heading" className="bg-white rounded-lg shadow p-5 mb-5" data-testid="portal-code-panel">
      <h2 id="portal-code-heading" className="font-semibold text-lg mb-2">Customer portal code</h2>
      <p className="text-sm text-gray-600">
        The customer enters this code, with their email address, on the portal's register page to create an online account for this invoice's customer.
        It is on the printed invoice, the public invoice page and the invoice email.
      </p>
      <p className="mt-3 font-mono text-2xl font-bold tracking-widest text-gray-900" data-testid="portal-code" aria-label="Portal code">
        {invoice.portalCode ?? 'No code'}
      </p>
      {notice && <Toast kind="success">{notice}</Toast>}
      {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
      {confirming ? (
        <div className="mt-3 rounded-md bg-amber-50 p-3 ring-1 ring-amber-200" role="group" aria-label="Confirm new portal code">
          <p className="text-sm text-amber-900">
            Create a new code for this invoice? <strong>The old code stops working immediately</strong>, including on copies already printed or emailed.
            Customers who already registered with it keep their accounts.
          </p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void regenerate()}>{busy ? 'Creating…' : 'Yes, replace the code'}</button>
            <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setConfirming(false)}>Keep the current code</button>
          </div>
        </div>
      ) : (
        <button type="button" className={`${secondaryButtonClass} mt-3`} onClick={() => { setConfirming(true); setNotice(null); setError(null); }}>Regenerate</button>
      )}
    </section>
  );
}
